import type { Color } from '@ml/config';
import type { Board, BoosterKind, Cell, OrbKind, Overlay } from './types.ts';

/**
 * Ký hiệu ô của golden-cases.md mục 0.1, dùng cho test và debug.
 * `R`, `Rk|Rf|Rh|Rv|Rd`, booster `R> R^ R* R+`, `@@` light ball, `E<n>` ngọc ấn, `..` rỗng,
 * lớp phủ `:L` (vĩnh viễn) / `:L<n>`, `:F<n>`, `:D<n>` (damage mặc định 0).
 */

const KIND_SUFFIX: Record<string, OrbKind> = {
  '': 'basic',
  k: 'kin',
  f: 'far',
  h: 'laneH',
  v: 'laneV',
  d: 'diag',
};
const SUFFIX_OF_KIND: Record<OrbKind, string> = {
  basic: '',
  kin: 'k',
  far: 'f',
  laneH: 'h',
  laneV: 'v',
  diag: 'd',
};
const BOOSTER_SUFFIX: Record<string, BoosterKind> = {
  '>': 'rocketH',
  '^': 'rocketV',
  '*': 'bomb',
  '+': 'propeller',
};
const SUFFIX_OF_BOOSTER: Record<BoosterKind, string> = {
  rocketH: '>',
  rocketV: '^',
  bomb: '*',
  propeller: '+',
};

function parseOverlay(s: string, token: string): Overlay {
  const m = /^:(L|F|D)(\d*)$/.exec(s);
  if (!m) throw new SyntaxError(`lớp phủ không hợp lệ trong "${token}"`);
  const n = m[2] === '' ? undefined : Number(m[2]);
  if (m[1] === 'L') return { type: 'lock', turns: n ?? 0 };
  if (n === undefined || n < 1) throw new SyntaxError(`thiếu số lớp/vòng trong "${token}"`);
  if (m[1] === 'F') return { type: 'freeze', layers: n };
  return { type: 'deadMark', turns: n, damage: 0 };
}

export function parseCell(token: string): Cell | null {
  if (token === '..') return null;
  if (token === '@@') return { t: 'lightball' };
  const ess = /^E(\d+)$/.exec(token);
  if (ess) return { t: 'essence', charge: Number(ess[1]) };
  const m = /^([RBGYP])([kfhvd]|[>^*+])?(:.*)?$/.exec(token);
  if (!m) throw new SyntaxError(`token ô không hợp lệ: "${token}"`);
  const color = m[1] as Color;
  const suffix = m[2] ?? '';
  const booster = BOOSTER_SUFFIX[suffix];
  if (booster) {
    if (m[3]) throw new SyntaxError(`booster không mang lớp phủ: "${token}"`);
    return { t: 'booster', kind: booster, color };
  }
  const kind = KIND_SUFFIX[suffix] as OrbKind;
  return m[3]
    ? { t: 'orb', kind, color, overlay: parseOverlay(m[3], token) }
    : { t: 'orb', kind, color };
}

export function formatCell(cell: Cell | null): string {
  if (!cell) return '..';
  switch (cell.t) {
    case 'lightball':
      return '@@';
    case 'essence':
      return `E${cell.charge}`;
    case 'booster':
      return cell.color + SUFFIX_OF_BOOSTER[cell.kind];
    case 'orb': {
      const base = cell.color + SUFFIX_OF_KIND[cell.kind];
      const o = cell.overlay;
      if (!o) return base;
      if (o.type === 'lock') return `${base}:L${o.turns === 0 ? '' : o.turns}`;
      if (o.type === 'freeze') return `${base}:F${o.layers}`;
      return `${base}:D${o.turns}`;
    }
  }
}

/**
 * Đọc bảng từ chuỗi: hàng cách nhau bằng xuống dòng hoặc `/`; bỏ dòng tiêu đề `x0 x1 …`
 * và tiền tố `y0:`. Mọi hàng phải cùng số ô.
 */
export function parseBoard(text: string): Board {
  const rows = text
    .split(/\n|\//)
    .map((l) => l.trim().replace(/^y\d+:\s*/, ''))
    .filter((l) => l !== '' && !/^x0\b/.test(l))
    .map((l) => l.split(/\s+/));
  if (rows.length === 0) throw new SyntaxError('bảng rỗng');
  const width = rows[0]!.length;
  for (const r of rows) {
    if (r.length !== width) throw new SyntaxError(`hàng có ${r.length} ô, mong ${width}`);
  }
  return { width, height: rows.length, cells: rows.flat().map(parseCell) };
}

export function formatBoard(board: Board): string {
  const lines: string[] = [];
  for (let y = 0; y < board.height; y++) {
    const row: string[] = [];
    for (let x = 0; x < board.width; x++)
      row.push(formatCell(board.cells[y * board.width + x] ?? null));
    lines.push(row.join(' '));
  }
  return lines.join('\n');
}
